import { useMemo } from "react";

import { type UseAccountReturnType, useAccount, useBalance } from "wagmi";

import { truncateAddress } from "@/lib/utils/mask";
import { emptyTokenBalance, formatTokenBalance } from "@/lib/utils/parse-token-amount";

import { emojiAvatarForAddress } from "@/subsystems/exchange/shared/components/emoji-avatar";
import { getGasFundsStatus } from "@/subsystems/exchange/shared/utils/get-gas-funds-status";

export function useWalletCore({ chainId }: { chainId?: number }) {
  const account: UseAccountReturnType = useAccount();

  const { address = null, isConnected = false } = account;

  const walletAddress = address;
  const isWalletConnected = isConnected;

  const walletAddressTruncated = truncateAddress(walletAddress);

  const nativeBalanceQuery = useBalance({
    address: walletAddress ?? undefined,
    chainId,
    query: {
      enabled: Boolean(walletAddress && chainId),
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  });

  async function invalidateNativeBalanceQuery() {
    await nativeBalanceQuery?.refetch();
  }

  const walletGasBalance = useMemo(() => {
    const native = nativeBalanceQuery.data;
    if (!native) return emptyTokenBalance;
    return formatTokenBalance(native.value, {
      decimals: native.decimals,
      symbol: native.symbol,
    });
  }, [nativeBalanceQuery.data]);

  const walletTheme = useMemo(
    () => (walletAddress ? emojiAvatarForAddress(walletAddress) : { color: null, emoji: null }),
    [walletAddress],
  );
  const walletGasStatus = useMemo(() => {
    const native = nativeBalanceQuery.data;
    return getGasFundsStatus(native?.value, native?.decimals);
  }, [nativeBalanceQuery.data]);

  return {
    invalidateNativeBalanceQuery,
    isWalletConnected,
    isWalletGasLoading: nativeBalanceQuery.isLoading,
    nativeBalanceQuery,
    walletAddress,
    walletAddressTruncated,
    walletGasBalance,
    walletGasStatus,
    walletThemeColor: walletTheme.color,
    walletThemeEmoji: walletTheme.emoji,
  };
}
